import React, { useState, useEffect, useRef } from 'react';
import { getSocket, isSocketConnected } from '../socket';
import { uploadFile, compressImageForUpload } from '../firebase';
import FitText from './FitText';

// Tasks tab: tickbox lists with draggable cards. Self-contained - fetches
// and listens on its own socket events.

function ProjectTaskBoard({ user, project }) {
  const [groups, setGroups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [renamingGroup, setRenamingGroup] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);
  const [addingTo, setAddingTo] = useState(null); // groupId with an open add-input
  const [newTaskText, setNewTaskText] = useState('');
  const [editingTask, setEditingTask] = useState(null); // taskId
  const [editText, setEditText] = useState('');
  const [drag, setDrag] = useState(null); // { taskId }
  const [dropHint, setDropHint] = useState(null); // { groupId, beforeTaskId|null }
  const [lightbox, setLightbox] = useState(null); // imageUrl
  const [uploadingImageFor, setUploadingImageFor] = useState(null); // taskId
  const newGroupInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const imageTargetRef = useRef(null); // taskId the picked image attaches to

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !isSocketConnected() || !project) return;

    socket.emit('project:tasks:get', { projectId: project.projectId });

    const pid = project.projectId;
    const onList = ({ projectId, groups: g, tasks: t }) => {
      if (projectId === pid) { setGroups(g || []); setTasks(t || []); }
    };
    const onGroupCreated = (g) => {
      if (g.projectId === pid) setGroups(prev => prev.some(x => x.groupId === g.groupId) ? prev : [...prev, g]);
    };
    const onGroupUpdated = ({ projectId, groupId, name }) => {
      if (projectId === pid) setGroups(prev => prev.map(g => g.groupId === groupId ? { ...g, name } : g));
    };
    const onGroupDeleted = ({ projectId, groupId }) => {
      if (projectId === pid) {
        setGroups(prev => prev.filter(g => g.groupId !== groupId));
        setTasks(prev => prev.filter(t => t.groupId !== groupId));
      }
    };
    const onTaskCreated = (t) => {
      if (t.projectId === pid) setTasks(prev => prev.some(x => x.taskId === t.taskId) ? prev : [...prev, t]);
    };
    const onTaskUpdated = ({ projectId, taskId, updates }) => {
      if (projectId === pid) setTasks(prev => prev.map(t => t.taskId === taskId ? { ...t, ...updates } : t));
    };
    const onTaskDeleted = ({ projectId, taskId }) => {
      if (projectId === pid) setTasks(prev => prev.filter(t => t.taskId !== taskId));
    };

    socket.on('project:tasks:list', onList);
    socket.on('project:taskgroup:created', onGroupCreated);
    socket.on('project:taskgroup:updated', onGroupUpdated);
    socket.on('project:taskgroup:deleted', onGroupDeleted);
    socket.on('project:task:created', onTaskCreated);
    socket.on('project:task:updated', onTaskUpdated);
    socket.on('project:task:deleted', onTaskDeleted);
    return () => {
      socket.off('project:tasks:list', onList);
      socket.off('project:taskgroup:created', onGroupCreated);
      socket.off('project:taskgroup:updated', onGroupUpdated);
      socket.off('project:taskgroup:deleted', onGroupDeleted);
      socket.off('project:task:created', onTaskCreated);
      socket.off('project:task:updated', onTaskUpdated);
      socket.off('project:task:deleted', onTaskDeleted);
    };
  }, [project]);

  const emit = (event, payload) => {
    const socket = getSocket();
    if (socket && isSocketConnected()) socket.emit(event, { projectId: project.projectId, ...payload });
  };

  const groupTasks = (groupId) =>
    tasks.filter(t => t.groupId === groupId).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;
    emit('project:taskgroup:create', { name: newGroupName.trim() });
    setNewGroupName('');
    setShowNewGroup(false);
  };

  const handleAddTask = (groupId) => {
    if (!newTaskText.trim()) return;
    emit('project:task:create', { groupId, text: newTaskText.trim() });
    setNewTaskText('');
    // keep the input open so several items can be typed in a row
  };

  const toggleDone = (task) => {
    // Optimistic tick so the checkbox feels instant
    setTasks(prev => prev.map(t => t.taskId === task.taskId ? { ...t, done: !task.done } : t));
    emit('project:task:update', { taskId: task.taskId, updates: { done: !task.done } });
  };

  const pickImageFor = (taskId) => {
    imageTargetRef.current = taskId;
    imageInputRef.current?.click();
  };

  const uploadTaskImage = async (taskId, file) => {
    if (!file || !file.type.startsWith('image/') || !taskId) return;
    setUploadingImageFor(taskId);
    try {
      const compressed = await compressImageForUpload(file, { maxDimension: 1280, quality: 0.8 });
      const path = `project-files/${project.projectId}/tasks/${Date.now()}_${file.name || 'pasted.png'}`;
      const url = await uploadFile(compressed || file, path, { contentType: (compressed || file).type });
      emit('project:task:update', { taskId, updates: { imageUrl: url } });
    } catch (err) {
      console.error('Task image upload failed:', err);
    }
    setUploadingImageFor(null);
  };

  const handleImagePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const taskId = imageTargetRef.current;
    imageTargetRef.current = null;
    await uploadTaskImage(taskId, file);
  };

  // Ctrl+V an image while hovering a card attaches it to that card
  const hoverTaskRef = useRef(null);
  useEffect(() => {
    const onPaste = (e) => {
      const taskId = hoverTaskRef.current;
      if (!taskId) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            uploadTaskImage(taskId, file);
          }
          return;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const commitEdit = (task) => {
    setEditingTask(null);
    const text = editText.trim();
    if (text && text !== task.text) {
      emit('project:task:update', { taskId: task.taskId, updates: { text } });
    }
  };

  // Drag + drop: compute a sortOrder between neighbours at the drop point
  const handleDropOn = (groupId, beforeTaskId) => {
    setDropHint(null);
    if (!drag) return;
    const dragged = tasks.find(t => t.taskId === drag.taskId);
    setDrag(null);
    if (!dragged) return;
    const list = groupTasks(groupId).filter(t => t.taskId !== dragged.taskId);
    let idx = beforeTaskId ? list.findIndex(t => t.taskId === beforeTaskId) : list.length;
    if (idx < 0) idx = list.length;
    const prev = idx > 0 ? (list[idx - 1].sortOrder || 0) : 0;
    const next = idx < list.length ? (list[idx].sortOrder || 0) : prev + 2000;
    const sortOrder = (prev + next) / 2;
    if (dragged.groupId === groupId && Math.abs((dragged.sortOrder || 0) - sortOrder) < 1e-6) return;
    setTasks(prevTasks => prevTasks.map(t => t.taskId === dragged.taskId ? { ...t, groupId, sortOrder } : t));
    emit('project:task:update', { taskId: dragged.taskId, updates: { groupId, sortOrder } });
  };

  return (
    <div className="pv-task-board">
      <div className="pv-task-board-header">
        <span className="pv-file-count">
          {tasks.filter(t => t.done).length}/{tasks.length} done
        </span>
        {showNewGroup ? (
          <div className="pv-task-newgroup">
            <input
              ref={newGroupInputRef}
              type="text"
              placeholder="List name..."
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateGroup();
                if (e.key === 'Escape') setShowNewGroup(false);
              }}
              autoFocus
            />
            <button onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
              <span className="material-symbols-outlined">check</span>
            </button>
          </div>
        ) : (
          <button className="pv-asset-add-btn" onClick={() => setShowNewGroup(true)}>
            <span className="material-symbols-outlined">add</span>
            New List
          </button>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="pv-empty-files">
          <span className="material-symbols-outlined">checklist</span>
          <p>No lists yet</p>
        </div>
      ) : (
        <div className="pv-task-columns">
          {groups.map(group => {
            const items = groupTasks(group.groupId);
            const doneCount = items.filter(t => t.done).length;
            return (
              <div
                key={group.groupId}
                className={`pv-task-column ${dropHint?.groupId === group.groupId && !dropHint.beforeTaskId ? 'drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); if (drag) setDropHint({ groupId: group.groupId, beforeTaskId: null }); }}
                onDrop={(e) => { e.preventDefault(); handleDropOn(group.groupId, null); }}
              >
                <div className="pv-task-column-header">
                  {renamingGroup === group.groupId ? (
                    <input
                      className="pv-rename-input"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (renameValue.trim()) emit('project:taskgroup:rename', { groupId: group.groupId, name: renameValue.trim() });
                          setRenamingGroup(null);
                        }
                        if (e.key === 'Escape') setRenamingGroup(null);
                      }}
                      onBlur={() => setRenamingGroup(null)}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="pv-task-column-title"
                      onDoubleClick={() => { setRenamingGroup(group.groupId); setRenameValue(group.name); }}
                      title="Double-click to rename"
                    >
                      <FitText text={group.name} max={14} min={10} />
                    </span>
                  )}
                  {renamingGroup !== group.groupId && (
                    <button
                      className="pv-task-column-rename"
                      onClick={() => { setRenamingGroup(group.groupId); setRenameValue(group.name); }}
                      title="Rename list"
                    >
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                  )}
                  <span className="pv-task-column-count">{doneCount}/{items.length}</span>
                  <button
                    className="pv-task-column-delete"
                    onClick={() => setConfirmDeleteGroup(group.groupId)}
                    title="Delete list"
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>

                {items.length > 0 && (
                  <div className="pv-task-progress">
                    <div
                      className="pv-task-progress-fill"
                      style={{ width: (items.length ? (doneCount / items.length) * 100 : 0) + '%' }}
                    />
                  </div>
                )}

                {confirmDeleteGroup === group.groupId && (
                  <div className="pv-task-group-confirm">
                    <span>Delete list{items.length ? ` + ${items.length} item${items.length !== 1 ? 's' : ''}` : ''}?</span>
                    <div>
                      <button
                        className="pv-confirm-yes"
                        onClick={() => { emit('project:taskgroup:delete', { groupId: group.groupId }); setConfirmDeleteGroup(null); }}
                      >
                        Delete
                      </button>
                      <button className="pv-confirm-no" onClick={() => setConfirmDeleteGroup(null)}>Cancel</button>
                    </div>
                  </div>
                )}

                <div className="pv-task-cards">
                  {items.map(task => (
                    <div
                      key={task.taskId}
                      className={`pv-task-card ${task.done ? 'done' : ''} ${dropHint?.groupId === group.groupId && dropHint?.beforeTaskId === task.taskId ? 'drop-before' : ''} ${drag?.taskId === task.taskId ? 'dragging' : ''}`}
                      draggable={editingTask !== task.taskId}
                      onMouseEnter={() => { hoverTaskRef.current = task.taskId; }}
                      onMouseLeave={() => { if (hoverTaskRef.current === task.taskId) hoverTaskRef.current = null; }}
                      onDragStart={() => setDrag({ taskId: task.taskId })}
                      onDragEnd={() => { setDrag(null); setDropHint(null); }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (drag && drag.taskId !== task.taskId) setDropHint({ groupId: group.groupId, beforeTaskId: task.taskId });
                      }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDropOn(group.groupId, task.taskId); }}
                    >
                      {task.imageUrl && (
                        <div className="pv-task-image" onClick={() => setLightbox(task.imageUrl)}>
                          <img src={task.imageUrl} alt="" loading="lazy" decoding="async" />
                          <button
                            className="pv-task-image-remove"
                            onClick={(e) => { e.stopPropagation(); emit('project:task:update', { taskId: task.taskId, updates: { imageUrl: null } }); }}
                            title="Remove image"
                          >
                            <span className="material-symbols-outlined">close</span>
                          </button>
                        </div>
                      )}
                      <div className="pv-task-card-row">
                      <button
                        className={`pv-task-check ${task.done ? 'checked' : ''}`}
                        onClick={() => toggleDone(task)}
                        title={task.done ? 'Mark not done' : 'Mark done'}
                      >
                        <span className="material-symbols-outlined">
                          {task.done ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                      </button>
                      {editingTask === task.taskId ? (
                        <input
                          className="pv-task-edit-input"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit(task);
                            if (e.key === 'Escape') setEditingTask(null);
                          }}
                          onBlur={() => commitEdit(task)}
                          autoFocus
                        />
                      ) : (
                        <span
                          className="pv-task-text"
                          onDoubleClick={() => { setEditingTask(task.taskId); setEditText(task.text); }}
                          title={task.done && task.doneByName ? `Done by ${task.doneBy === user.uid ? 'you' : task.doneByName}` : 'Double-click to edit'}
                        >
                          {task.text}
                        </span>
                      )}
                      <button
                        className="pv-task-attach"
                        onClick={() => pickImageFor(task.taskId)}
                        title={task.imageUrl ? 'Replace image' : 'Attach image'}
                        disabled={uploadingImageFor === task.taskId}
                      >
                        <span className="material-symbols-outlined">
                          {uploadingImageFor === task.taskId ? 'hourglass_top' : 'image'}
                        </span>
                      </button>
                      <button
                        className="pv-task-delete"
                        onClick={() => emit('project:task:delete', { taskId: task.taskId })}
                        title="Delete item"
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                      </div>
                    </div>
                  ))}

                  {addingTo === group.groupId ? (
                    <div className="pv-task-add-row">
                      <input
                        type="text"
                        placeholder="New item..."
                        value={newTaskText}
                        onChange={(e) => setNewTaskText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAddTask(group.groupId);
                          if (e.key === 'Escape') { setAddingTo(null); setNewTaskText(''); }
                        }}
                        onBlur={() => { if (!newTaskText.trim()) { setAddingTo(null); setNewTaskText(''); } }}
                        autoFocus
                      />
                      <button onClick={() => handleAddTask(group.groupId)} disabled={!newTaskText.trim()}>
                        <span className="material-symbols-outlined">check</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      className="pv-task-add-btn"
                      onClick={() => { setAddingTo(group.groupId); setNewTaskText(''); }}
                    >
                      <span className="material-symbols-outlined">add</span>
                      Add item
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImagePicked}
      />

      {lightbox && (
        <div className="pv-task-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
        </div>
      )}
    </div>
  );
}

export default ProjectTaskBoard;
